import React, { useEffect, useRef, useState } from 'react';
import { Icons } from '@/utils/Icons';
import { useLazyGetEventsBySearchQuery } from '@/store/api/sportsApi';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '@/utils';
import dayjs from 'dayjs';

type SearchDropdownProps = {
  variant?: 'compact' | 'default';
};

const SearchDropdown: React.FC<SearchDropdownProps> = ({ variant = 'default' }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [trigger, { data, isFetching }] = useLazyGetEventsBySearchQuery();

  // Debounce
  useEffect(() => {
    const delay = setTimeout(() => {
      if (query.trim())
        trigger({
          search: query,
          fromDate: dayjs().startOf('day').toISOString(),
          toDate: dayjs().add(1, 'day').endOf('day').toISOString(),
        });
    }, 500);

    return () => clearTimeout(delay);
  }, [query]);

  // Outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      setQuery('');
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEventClick = (sport: string, eventId: string) => {
    navigate(`/game-details/${sport}/${eventId}`);
    setQuery('');
    setShowSearch(false);
  };

  const showDropdown = variant === 'default' ? query : showSearch && query;

  return (
    <div ref={wrapperRef} className="relative flex items-center gap-2">
      {/* ===== DEFAULT FULL INPUT ===== */}
      {variant === 'default' && (
        <div className="border border-gray-400 h-[34px] flex items-center px-2 bg-white">
          <input
            type="search"
            placeholder="Search here"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-transparent outline-none text-[14px] placeholder-gray-600"
          />
        </div>
      )}

      {/* ===== COMPACT ICON SEARCH ===== */}
      {variant === 'compact' && (
        <>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              showSearch ? 'w-[150px] opacity-100' : 'w-0 opacity-0'
            }`}
          >
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-[150px] h-9 px-3 outline-none text-black"
              placeholder="Search..."
            />
          </div>

          <Icons.SearchPlus
            onClick={() => {
              setShowSearch((prev) => !prev);
              setQuery('');
            }}
            className="text-2xl cursor-pointer text-white xl:black"
          />
        </>
      )}

      {/* ===== DROPDOWN ===== */}
      {showDropdown && (
        <div className="absolute top-12 left-0 w-[260px] bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto z-50 text-black">
          {isFetching && <div className="px-4 py-2 text-gray-500 text-sm">Searching...</div>}

          {!isFetching &&
            data?.data &&
            Object.entries(data.data).map(([sport, events]) => (
              <div key={sport}>
                <div className="px-4 py-2 bg-primary text-white text-sm font-semibold">
                  {sport}
                </div>

                {events.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleEventClick(item.sport, item.id)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-t text-base"
                  >
                    <div className="text-base text-body-strong">{item.name}</div>
                    <div className="text-base text-body-strong">{formatDateTime(item.startTime)}</div>
                  </div>
                ))}
              </div>
            ))}

          {!isFetching && data?.data && Object.values(data.data).flat().length === 0 && (
            <div className="px-4 py-2 text-gray-500 text-sm">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
